import React from 'react'
import { useRouterState } from '@tanstack/react-router'
import { AuthDialog } from '../AuthDialog'
import { Footer } from '../footer'
import { PersonalInfoDialog } from '../PersonalInfoDialog'
import { SideBarLayout } from '../SideBar/SideBarLayout'
import { SidebarProvider } from '@duck/ui'
import { BrowserTranslationDialog } from '../BrowserTranslationDialog'
import { PickWeaponDialog } from '../Workspace/PickWeaponDialog'
import { UserVoiceDialog } from '@/components/UserVoiceDialog/UserVoiceDialog'


interface GeneralLayoutComponentProps {
  children: React.ReactNode;
}

const widePages = ['/challenge/', '/submission/']
const collapsedSidebarPages = ['/submission/']
// Chrome-free pages: the challenge workspace and submission views are wide AND
// fill the viewport, so a footer would be out of place. The challenges browser
// is wide too, but it's a normal scrolling page — it keeps the footer.
const footerlessPages = ['/challenge/', '/submission/']

// The challenges browser is wide: both the available list and any
// /challenges/<slug> deep link (which renders the same page). The completed
// list is a separate, normal-width page, so it's excluded.
const isChallengesBrowser = (pathname: string) =>
  pathname.startsWith('/challenges/') && !pathname.startsWith('/challenges/completed')

const MainContentWrapper = ({ fullWidth = false, children }: { fullWidth?: boolean, children: React.ReactNode }) => {
  if (fullWidth) {
    return (
      <div className="flex flex-col min-h-[560px] flex-1">
        {children}
      </div>
    )
  }
  return (
    <main
      className={`flex-1 flex justify-center px-4 md:px-6 lg:px-8 w-full min-h-screen md:max-w-[calc(100dvw-var(--sidebar-current-width))] overflow-x-clip`}
    >
      <div className={`flex w-full justify-center mb-16 max-w-6xl pt-4 md:pt-8`}>
        {children}
      </div>
    </main>
  )
}

export const GeneralLayoutComponent: React.FC<GeneralLayoutComponentProps> = ({
  children,
}) => {
  // Use resolvedLocation (committed/rendered location), not the pending one.
  // TanStack Router wraps navigation in React.startTransition, so `location`
  // updates synchronously while the Outlet swap is deferred — reading it here
  // switches the wrapper to full-width before the new route mounts, making the
  // outgoing page flash wider for a frame.
  const pathname = useRouterState({
    select: (s) => (s.resolvedLocation ?? s.location).pathname,
  })

  const shouldBeWide = widePages.some((route) => pathname.startsWith(route)) || isChallengesBrowser(pathname)
  const shouldStartCollapsed = collapsedSidebarPages.some((route) => pathname.startsWith(route))
  const showFooter = !footerlessPages.some((route) => pathname.startsWith(route))
  return (
    <SidebarProvider defaultOpen={!shouldStartCollapsed}>
      <SideBarLayout>
        <div className={`flex flex-col flex-1`}>
          <MainContentWrapper fullWidth={shouldBeWide}>
            {children}
          </MainContentWrapper>
        </div>

        {showFooter && <Footer />}

        <AuthDialog />
        <PersonalInfoDialog />
        {/*<YouHaveAssignmentsDialog />*/}
        <BrowserTranslationDialog />
        <PickWeaponDialog />
        <UserVoiceDialog />
      </SideBarLayout>
    </SidebarProvider>
  )
}
